import React, { createContext, useContext, useReducer, useEffect } from 'react'
import { apiService } from '../services/api'

const AuthContext = createContext()

const initialState = {
  user: null,
  profile: null,
  isAuthenticated: false,
  isAdmin: false,
  loading: true,
  error: null
}

function authReducer(state, action) {
  switch (action.type) {
    case 'AUTH_START':
      return { ...state, loading: true, error: null }

    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        profile: action.payload.profile,
        isAuthenticated: true,
        isAdmin: action.payload.profile?.role === 'admin',
        loading: false,
        error: null
      }

    case 'AUTH_FAILURE':
      return {
        ...state,
        user: null,
        profile: null,
        isAuthenticated: false,
        isAdmin: false,
        loading: false,
        error: action.payload
      }

    case 'AUTH_LOGOUT':
      return {
        ...initialState,
        loading: false
      }

    default:
      return state
  }
}

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState)

  useEffect(() => {
    // Verificar si hay token guardado al cargar la app y validarlo con el backend
    const checkSession = async () => {
      const token = localStorage.getItem('authToken');
      const userData = localStorage.getItem('userData');
      if (token && userData) {
        try {
          // Validar el token con el backend
          const me = await apiService.request('/auth/me');
          if (me && me.user && me.profile) {
            dispatch({
              type: 'AUTH_SUCCESS',
              payload: { user: me.user, profile: me.profile }
            });
            // Actualizar datos en localStorage
            localStorage.setItem('userData', JSON.stringify({ user: me.user, profile: me.profile }));
          } else {
            // Token inválido o expirado
            console.warn('Token validation returned invalid data');
            localStorage.removeItem('authToken');
            localStorage.removeItem('userData');
            dispatch({ type: 'AUTH_FAILURE', payload: 'Sesión inválida' });
          }
        } catch (error) {
          // Solo limpiar la sesión si es un error de autenticación (401/403)
          // No limpiar por errores de red temporales
          if (error.message && error.message.includes('Sesión expirada')) {
            console.warn('Token expired, clearing session');
            localStorage.removeItem('authToken');
            localStorage.removeItem('userData');
            dispatch({ type: 'AUTH_FAILURE', payload: 'Sesión inválida' });
          } else {
            // Error de red u otro temporal - mantener la sesión si hay datos válidos en localStorage
            console.warn('Error validating session, but keeping localStorage data:', error.message);
            try {
              const parsedUserData = JSON.parse(userData);
              if (parsedUserData.user && parsedUserData.profile) {
                dispatch({
                  type: 'AUTH_SUCCESS',
                  payload: { user: parsedUserData.user, profile: parsedUserData.profile }
                });
              } else {
                dispatch({ type: 'AUTH_FAILURE', payload: null });
              }
            } catch (parseError) {
              console.error('Error parsing localStorage userData:', parseError);
              dispatch({ type: 'AUTH_FAILURE', payload: null });
            }
          }
        }
      } else {
        dispatch({ type: 'AUTH_FAILURE', payload: null });
      }
    };
    checkSession();
  }, []);

  const login = async (email, password) => {
    dispatch({ type: 'AUTH_START' })

    try {
      const response = await apiService.login(email, password)

      // Guardar datos del usuario
      localStorage.setItem('userData', JSON.stringify({
        user: response.user,
        profile: response.profile
      }))

      dispatch({
        type: 'AUTH_SUCCESS',
        payload: {
          user: response.user,
          profile: response.profile
        }
      })

      return response
    } catch (error) {
      dispatch({ type: 'AUTH_FAILURE', payload: error.message })
      throw error
    }
  }

  const logout = async () => {
    try {
      await apiService.logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      localStorage.removeItem('userData')
      dispatch({ type: 'AUTH_LOGOUT' })
    }
  }

  const value = {
    ...state,
    login,
    logout
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}